import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AuthLayout, FieldLabel } from '@/components/AuthLayout';
import { AuthPending } from '@/components/AuthPending';
import { SCOPE_LABELS, type ApiTokenScope } from '@/lib/api-tokens';
import { saveMessage } from '@/lib/apps';
import { useApproveOAuthRequest, useDenyOAuthRequest, useOAuthRequest } from '@/lib/oauth';

/**
 * "Allow ChatGPT to work on your apps and posts?"
 *
 * Where ChatGPT's or Claude's "Connect" lands after the API has checked
 * their request: the name they registered under, where the browser goes
 * back to, and what they may do — each scope a box, all ticked, any of
 * them untickable. The password again, because this is a credential
 * leaving the browser for three months, exactly like a token made in
 * Settings. Either answer ends with the browser sent back to the app; the
 * page itself has nowhere else to go.
 */
export default function OAuthConsentPage() {
  const [params] = useSearchParams();
  const id = params.get('request');
  const request = useOAuthRequest(id);
  const approve = useApproveOAuthRequest();
  const deny = useDenyOAuthRequest();
  const [password, setPassword] = useState('');
  const [scopes, setScopes] = useState<ApiTokenScope[] | null>(null);
  const [leaving, setLeaving] = useState(false);

  if (id === null || request.data === null || request.isError) {
    return (
      <AuthLayout
        title="Nothing to allow"
        subtitle="This sign-in request has expired or was already answered. Go back to the app and start the connection again."
        footnote="A request is good for ten minutes."
      >
        <Link to="/dashboard" className="btn-quiet focus-ring cursor-pointer self-start">
          Go to the admin
        </Link>
      </AuthLayout>
    );
  }
  if (!request.data) return <AuthPending />;

  const pending = request.data;
  const ticked = scopes ?? pending.scopes;
  const toggle = (scope: ApiTokenScope) =>
    setScopes(ticked.includes(scope) ? ticked.filter((one) => one !== scope) : [...ticked, scope]);
  const busy = approve.isPending || deny.isPending || leaving;
  const canAllow = password !== '' && ticked.length > 0 && !busy;

  const go = (url: string | null) => {
    setLeaving(true);
    // Nothing on this site to show afterwards: the answer belongs to the app that asked.
    window.location.assign(url ?? '/dashboard');
  };
  const allow = (event: FormEvent) => {
    event.preventDefault();
    if (!canAllow) return;
    approve.mutate(
      { id: pending.id, scopes: ticked, password },
      { onSuccess: go, onError: () => setPassword('') },
    );
  };
  const refuse = () => {
    if (busy) return;
    deny.mutate(pending.id, { onSuccess: go });
  };

  return (
    <AuthLayout
      title={`Allow ${pending.clientName}?`}
      subtitle={`${pending.clientName} wants to work on your apps and posts through the MCP server, as you.`}
      footnote="The connection lasts 90 days and can be revoked at any time in Settings › Connections. Changing your password revokes it."
    >
      {/*
        The name above is whatever the app registered itself as; anyone can
        register as "ChatGPT". The address is the thing the server checked,
        and the thing the code goes to — so it is the line to read before
        typing a password.
      */}
      <div
        className="flex flex-col gap-1 rounded-lg px-3 py-2"
        style={{ background: 'var(--panel2)', border: '1px solid var(--line)' }}
      >
        <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
          Goes back to
        </span>
        <span className="font-mono text-[13px] font-medium" data-redirect-host>
          {pending.redirectHost}
        </span>
      </div>
      <form className="flex flex-col gap-3.5" onSubmit={allow}>
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-2">
            <FieldLabel>What it may do</FieldLabel>
          </legend>
          {pending.scopes.map((scope) => (
            <label key={scope} className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={ticked.includes(scope)}
                onChange={() => toggle(scope)}
                disabled={busy}
              />
              {SCOPE_LABELS[scope]}
              <code className="font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
                {scope}
              </code>
            </label>
          ))}
        </fieldset>

        <label className="flex flex-col gap-1.5">
          <FieldLabel>Your password</FieldLabel>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="field field-auth w-full"
            autoComplete="current-password"
            autoFocus
            disabled={busy}
          />
          <span className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
            Asked again on purpose: this hands {pending.clientName} a credential that leaves the
            browser.
          </span>
        </label>

        {approve.isError ? (
          <span className="text-[12px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(approve.error)}
          </span>
        ) : null}
        {deny.isError ? (
          <span className="text-[12px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(deny.error)}
          </span>
        ) : null}

        <span className="flex items-center gap-2.5">
          <button
            type="submit"
            disabled={!canAllow}
            className="btn-accent focus-ring min-h-[42px] flex-1 cursor-pointer p-[11px] text-sm disabled:cursor-default disabled:opacity-50"
          >
            {approve.isPending || leaving ? 'Allowing…' : 'Allow'}
          </button>
          <button
            type="button"
            onClick={refuse}
            disabled={busy}
            className="btn-quiet focus-ring min-h-[42px] cursor-pointer px-3 text-sm disabled:cursor-default disabled:opacity-50"
          >
            Don't allow
          </button>
        </span>
      </form>
    </AuthLayout>
  );
}
